/**
 * Apartmentspalte (Board „Vertrieb“): Name, Zimmer eines Verbunds eingerückt unter der
 * Gesamtwohnung, und – sobald es Übertragungen gibt – ihre Zahl als Marke. Rot heißt
 * fehlgeschlagen, Bernstein läuft oder wurde nicht gesendet; „gesendet ohne Rückvergleich“ ist der
 * Normalfall dieses Betriebs und deshalb kein Signal. Das Popover nennt nur den Status – Zeitraum
 * und Zeiten je Übertragung liefert das Lesemodell nicht; die Werte zeigt der Verlauf im Apartment.
 */
import * as Popover from '@radix-ui/react-popover';
import { Link } from '@tanstack/react-router';
import { ChevronRight, X } from 'lucide-react';
import type { AvailabilityDistributionOperation, DynamicPricingMode } from '@/shared/api';
import { ariOperationLabel } from '@/features/properties/ari-edit';
import { cn } from '@/shared/lib/cn';
import { AvailabilityDistributionOperationList } from '../AvailabilityDistributionOperationList';
import { ModeChip } from '../automation/AutomationMarks';

type OperationStatus = AvailabilityDistributionOperation['status'];

const TONE: Partial<Record<OperationStatus, string>> = {
  Failed: 'bg-sealwax-tint text-sealwax',
  Queued: 'bg-amber-tint text-amber-ink',
  Retrying: 'bg-amber-tint text-amber-ink',
  Suppressed: 'bg-amber-tint text-amber-ink',
};

interface ApartmentRowLabelProps {
  propertyId: string;
  name: string;
  operations: AvailabilityDistributionOperation[];
  /** Der Name führt in den Monat dieses Apartments. */
  onOpenMonth?: (propertyId: string) => void;
  /** Modus der Preisautomatik (nur Admin/Staff). */
  mode?: DynamicPricingMode | null;
  /** Zimmer eines Verbunds, dessen Gesamtwohnung direkt darüber steht. */
  child?: boolean;
}

export function ApartmentRowLabel(props: ApartmentRowLabelProps) {
  const { name, operations, child } = props;
  const latest = operations[0];
  // Der Server liefert höchstens die letzten 20; mehr heißt „20+“.
  const total = operations.length >= 20 ? '20+' : String(operations.length);
  const attention = latest && TONE[latest.status] ? `${ariOperationLabel(latest.status)} · ` : '';
  const count = `${attention}${total === '1' ? '1 Übertragung' : `${total} Übertragungen`}`;
  return (
    <div
      className={cn(
        'border-hairline sticky left-0 z-20 flex min-w-0 flex-col justify-center border-r bg-inherit pr-3.5',
        child
          ? 'bg-[linear-gradient(90deg,transparent_22px,var(--color-line)_22px,var(--color-line)_23px,transparent_23px)] pl-10'
          : 'pl-[18px]',
      )}
    >
      {props.onOpenMonth ? (
        <button
          type="button"
          onClick={() => props.onOpenMonth?.(props.propertyId)}
          aria-label={`Monat von ${name} öffnen`}
          className="text-ink hover:text-forest relative flex min-h-8 min-w-0 items-center gap-1 self-start text-left text-sm font-bold before:absolute before:-inset-y-1.5 before:inset-x-0 before:content-['']"
        >
          <span className="truncate" title={name}>
            {name}
          </span>
          <ChevronRight size={13} aria-hidden className="shrink-0" />
        </button>
      ) : (
        <span className="text-ink truncate text-sm font-bold" title={name}>
          {name}
        </span>
      )}
      <span className="flex min-w-0 items-center gap-1.5">
        {props.mode && <ModeChip mode={props.mode} />}
        {latest && (
          <Popover.Root>
            <Popover.Trigger asChild>
              <button
                type="button"
                aria-label={`${count} für ${name} anzeigen`}
                // 44 px Trefferfläche um die 22-px-Marke (§9), ohne die 64-px-Zeile zu sprengen.
                className="relative flex min-w-0 items-center before:absolute before:-inset-y-[11px] before:inset-x-0 before:content-['']"
              >
                <span
                  className={cn(
                    'rounded-pill flex h-[22px] max-w-full items-center px-2 text-[11px] font-bold',
                    TONE[latest.status] ?? 'bg-card-alt text-ink-soft',
                  )}
                >
                  <span className="truncate">{count}</span>
                </span>
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                side="right"
                align="start"
                sideOffset={8}
                collisionPadding={16}
                className="bg-card border-hairline shadow-panel rounded-tile z-50 max-h-[60dvh] w-[min(380px,calc(100vw-32px))] overflow-y-auto border p-4"
              >
                <div className="flex items-center gap-2">
                  <p className="text-ink grow text-[15px] font-bold">{name} · Übertragungen</p>
                  <Popover.Close
                    aria-label="Schließen"
                    className="rounded-pill border-hairline grid size-11 place-items-center border"
                  >
                    <X size={14} aria-hidden />
                  </Popover.Close>
                </div>
                <AvailabilityDistributionOperationList operations={operations} />
                <Link
                  to="/properties/$id/preise"
                  params={{ id: props.propertyId }}
                  className="text-forest mt-1 inline-flex min-h-11 items-center text-[13px] font-bold underline-offset-2 hover:underline"
                >
                  Verlauf im Apartment öffnen
                </Link>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
      </span>
    </div>
  );
}
